import { TITLE_CHAR_LIMIT, TITLE_TARGET_MIN } from './title-compliance-constants';

export interface RawScraperRow {
  info_asin?: string;
  brand?: string;
  brand_url?: string;
  product_category?: string;
  product_group?: string;
  Rating?: string | number;
  total_reviews?: string | number;
  pASIN?: string;
  variant_asins?: string;
  name?: string;
  feature_bullets_count?: string | number;
  feature_bullets?: string;
  full_description?: string;
  count_of_images?: string | number;
  URI?: string;
  aplus_present?: string | boolean;
  climate_pledge_friendly?: string | boolean;
  amazons_choice_flag?: string | boolean;
  best_seller_flag?: string | boolean;
  prime_flag?: string | boolean;
  videos_count?: string | number;
  video?: string;
}

/**
 * TEMPORARY — video scoring is switched OFF (2026-07-20).
 *
 * The ScraperAPI feed does not return video data for the ASINs we scrape
 * (see the DSH-32549 note in CommerceScraperUtils.ts). With video treated as a
 * scored criterion, every listing took an automatic 5-pt FAIL and the portfolio
 * bottleneck / KPI panels flagged 100% of ASINs as "missing video" — a data gap
 * being reported as a listing defect.
 *
 * While this flag is `false`:
 *   - video is excluded from the Portfolio Health score (the finalScore is
 *     normalized over the remaining criteria, so scores are not depressed),
 *   - the video criterion no longer counts as a fail,
 *   - all video UI (KPI badge, drawer row, guidance card, bottleneck check) is hidden.
 *
 * The video config, types, and parsing are intentionally LEFT IN PLACE. If/when
 * the scraper starts returning video, flip this to `true` and everything returns
 * exactly as before — no other changes required.
 */
export const VIDEO_SCORING_ENABLED = false;

export const ScoreStatus = {
  PASS: 'pass',
  WARNING: 'warning',
  FAIL: 'fail'
} as const;
export type ScoreStatusType = (typeof ScoreStatus)[keyof typeof ScoreStatus];

export interface ScoreItem {
  score: number;
  maxScore: number;
  actualValue: string | number;
  status: ScoreStatusType;
  label: string;
  criteriaText: string;
}

export const Grade = {
  A: 'A',
  B: 'B',
  C: 'C',
  D: 'D',
  F: 'F'
} as const;
export type GradeType = (typeof Grade)[keyof typeof Grade];

export interface AuditedProduct {
  id: string;
  asin: string;
  brand: string;
  brandUrl: string;
  category: string;
  group: string;
  rating: number;
  totalReviews: number;
  pAsin: string;
  variantAsins: string[];
  title: string;
  featureBulletsCount: number;
  featureBullets: string[];
  fullDescription: string;
  countOfImages: number;
  uri: string;
  aplusPresent: boolean;
  climatePledgeFriendly: boolean;
  amazonsChoiceFlag: boolean;
  bestSellerFlag: boolean;
  primeFlag: boolean;
  videosCount: number;

  bulletsScore: ScoreItem;
  titleScore: ScoreItem;
  descriptionScore: ScoreItem;
  imagesScore: ScoreItem;
  ratingScore: ScoreItem;
  reviewsScore: ScoreItem;
  videosScore: ScoreItem;
  aplusScore: ScoreItem;

  finalScore: number;
  grade: GradeType;
  warningCount: number;
  failCount: number;

  alexaReadinessScore: number;
}

export interface BestPracticeFinding {
  id: string;
  recommendation: string;
  rationale: string;
  applies_to: 'portfolio_health' | 'alexa_readiness' | 'both';
  source_name: string;
  source_url: string;
  source_tier: 1 | 2 | 3;
  confidence: 'high' | 'medium' | 'low';
  date_published: string;
  tags: string[];
  category: string;
}

export interface BestPracticesData {
  metadata: {
    run_date: string;
    research_window_months: number;
    cutoff_date: string;
    agent_version: string;
    /**
     * When the fixture's content was last hand-revised, independent of
     * `run_date` (when the research agent last ran). Set whenever a finding is
     * edited outside an agent run so prompts and the Guidance Center cite the
     * date the guidance actually changed. Optional: absent until first revised.
     */
    last_revised?: string;
  };
  categories: {
    title: { findings: BestPracticeFinding[] };
    bullets: { findings: BestPracticeFinding[] };
    description: { findings: BestPracticeFinding[] };
    alexa_for_shopping: { findings: BestPracticeFinding[] };
  };
  deprecated_practices: { practice: string; replaced_by: string; source: string }[];
  coverage_gaps: string[];
}

/**
 * Pass thresholds (weight, passMin, passMax) were set by Candice and are fixed,
 * except the title band — both its ends are derived from the title-length
 * constants so the rubric cannot disagree with the rest of the tool. See the
 * comment on SCORING_CONFIG.title.
 *
 * Warning-band point values use graduated partial credit: fixable content fields
 * (bullets, title, description, images) earn 40–67% of the max in the warning
 * band; operational metrics (rating, reviews) earn 50%.
 */
export interface BandedCriterion {
  weight: number;
  warningPoints: number;
  criteriaText: string;
}

export interface BinaryCriterion {
  weight: number;
  criteriaText: string;
}

export interface ScoringConfig {
  bullets: BandedCriterion & { passMin: number; warnMin: number; warnMax: number };
  /**
   * Both ends are derived: passMax is Amazon's hard cap (TITLE_CHAR_LIMIT) — over
   * it scores 0, not partial credit — and passMin is the same floor the AI
   * rewriter writes to (TITLE_TARGET_MIN), so the grader never fails a title the
   * tool itself produced.
   */
  title: BandedCriterion & {
    passMin: number;
    passMax: number;
    warnLowMin: number;
    warnLowMax: number;
  };
  description: BandedCriterion & { passMin: number; warnMin: number; warnMax: number };
  images: BandedCriterion & { passMin: number; warnExact: number };
  rating: BandedCriterion & { passMin: number; warnMin: number };
  reviews: BandedCriterion & { passMin: number; warnMin: number; warnMax: number };
  videos: BinaryCriterion;
  aplus: BinaryCriterion;
  alexaProxy: {
    dimensionPoints: number;
    bulletsTarget: number;
    bulletMaxChars: number;
    /**
     * Lower bound for the title gate. This gate is binary — full dimension
     * points or zero, with no warning tier — so it tracks the PASS floor
     * (TITLE_TARGET_MIN), not Portfolio Health's warning floor.
     */
    titleMin: number;
    /** Upper bound for full title credit — Amazon's hard cap; nothing above this is achievable. */
    titleIdealMax: number;
    descMin: number;
  };
}

export const SCORING_CONFIG: ScoringConfig = {
  bullets: {
    weight: 15,
    warningPoints: 8,
    passMin: 5,
    warnMin: 2,
    warnMax: 4,
    criteriaText:
      '0–1 bullets = 0 pts | 2–4 bullets = 8 pts (warning) | 5–8 bullets = 15 pts (full)'
  },
  // Both ends of this band are derived, not typed in, so neither can drift from
  // the constants the rest of the tool already enforces:
  //
  //   passMax = TITLE_CHAR_LIMIT (75)  — Amazon's hard cap. Reconciled 2026-09-29
  //     from the old 200-char band, which predated the cap (effective
  //     2026-07-27). Over it, Amazon truncates, so it scores 0, not partial
  //     credit. Product decision (Candice).
  //
  //   passMin = TITLE_TARGET_MIN (55)  — the same floor the AI rewriter writes
  //     to. Lowered 2026-10-01 from 65. At 65 the grader was stricter than the
  //     generator: a rewrite the title path produced and Title Compliance passed
  //     could still score 10/25 or 0/25 here, so the tool failed its own output
  //     (the SmartyPants B0DYBHFBH8 rewrite lands at 51 chars). Grading now
  //     matches generation. Getting generated titles longer is the real fix and
  //     is still open — see the category target bands in ai-optimize-utils.ts,
  //     which deliberately still aim higher (~55-75) than this floor.
  //
  // The warning band keeps its 5-char width and sits directly beneath passMin.
  title: {
    weight: 25,
    warningPoints: 10,
    passMin: TITLE_TARGET_MIN,
    passMax: TITLE_CHAR_LIMIT,
    warnLowMin: TITLE_TARGET_MIN - 5,
    warnLowMax: TITLE_TARGET_MIN - 1,
    criteriaText: `0–${TITLE_TARGET_MIN - 6} chars = 0 pts (fail, too short) | ${TITLE_TARGET_MIN - 5}–${TITLE_TARGET_MIN - 1} chars = 10 pts (warning, too short) | ${TITLE_TARGET_MIN}–${TITLE_CHAR_LIMIT} chars = 25 pts (full) | ${TITLE_CHAR_LIMIT + 1}+ chars = 0 pts (fail — exceeds Amazon's ${TITLE_CHAR_LIMIT}-char cap and gets truncated)`
  },
  description: {
    weight: 5,
    warningPoints: 3,
    passMin: 801,
    warnMin: 301,
    warnMax: 800,
    criteriaText:
      '0–300 chars = 0 pts | 301–800 chars = 3 pts (warning) | 801–2000 chars = 5 pts (full)'
  },
  images: {
    weight: 15,
    warningPoints: 10,
    passMin: 5,
    warnExact: 4,
    criteriaText: '0–3 images = 0 pts | 4 images = 10 pts (warning) | 5–12 images = 15 pts (full)'
  },
  rating: {
    weight: 10,
    warningPoints: 5,
    passMin: 4.2,
    warnMin: 4.0,
    criteriaText:
      '0–3.9 stars = 0 pts | 4.0–4.1 stars = 5 pts (warning) | 4.2–5.0 stars = 10 pts (full)'
  },
  reviews: {
    weight: 15,
    warningPoints: 8,
    passMin: 100,
    warnMin: 21,
    warnMax: 99,
    criteriaText:
      '0–20 reviews = 0 pts | 21–99 reviews = 8 pts (warning) | 100+ reviews = 15 pts (full)'
  },
  videos: {
    weight: 5,
    criteriaText: '0 videos = 0% pts | 1+ videos = Full 5% pts'
  },
  aplus: {
    weight: 10,
    criteriaText: 'No A+ content = 0% pts | A+ content present = Full 10% pts'
  },
  alexaProxy: {
    dimensionPoints: 25,
    bulletsTarget: 5,
    bulletMaxChars: 150,
    titleMin: TITLE_TARGET_MIN,
    titleIdealMax: TITLE_CHAR_LIMIT,
    descMin: 801
  }
};
